import React from 'react';
import { DefaultDateTimeFormatter } from '../../lib';
import { D3Tooltip } from './D3Tooltip';
import type { MultiLineData, SingleLineData } from './LineChartUtil';
import { getValueWithUnit, isMultiLineData, margin } from './LineChartUtil';

type CommonLineChartTooltipProps = {
  data: SingleLineData | MultiLineData | null;
  visible: boolean;
  position: {
    x: number;
    y: number;
  };
  firstLineLegend: string;
  secondLineLegend?: string;
  constrainX?: number;
};

export type LineChartTooltipProps =
  | (CommonLineChartTooltipProps & {
      showHoverGuidelines: true;
      height: number;
      width: number;
    })
  | (CommonLineChartTooltipProps & {
      showHoverGuidelines?: false;
      height?: never;
      width?: never;
    });

export function LineChartTooltip({
  data,
  visible,
  position,
  firstLineLegend,
  secondLineLegend,
  constrainX,
  showHoverGuidelines,
  width,
  height
}: Readonly<LineChartTooltipProps>): JSX.Element {
  const firstLineValue = getValueWithUnit(
    data?.y.firstLine.value,
    data?.y.firstLine.unit
  );
  const isMultiLine = isMultiLineData(data);
  const secondLineValue =
    isMultiLine &&
    getValueWithUnit(data.y.secondLine.value, data.y.secondLine.unit);

  const formattedDate = DefaultDateTimeFormatter.formatDate(data?.x ?? '');

  return (
    <>
      {visible && showHoverGuidelines && (
        <>
          <line
            className="hover-dashed-line"
            x1={margin.left}
            x2={width + margin.left}
            y1={position.y}
            y2={position.y}
          />
          <line
            className="hover-dashed-line"
            x1={position.x}
            x2={position.x}
            y1={margin.top}
            y2={height - margin.bottom}
          />
        </>
      )}
      <D3Tooltip
        visible={visible}
        position={position}
        className="line-chart-tooltip"
        width={
          // determine width depending on the data
          50 +
          Math.max(
            formattedDate.length,
            data?.y.firstLine.optionalText?.length ?? 0,
            ((isMultiLine &&
              data.y.secondLine.optionalText?.length) as number) ?? 0,
            // to represent the padding between elements, we're adding 5 to the length
            (firstLineLegend + firstLineValue).length + 5,
            isMultiLine
              ? ((secondLineLegend ?? '') + secondLineValue).length + 5
              : 0
          ) *
            7
        }
        height={isMultiLine ? 100 : 80}
        constrainX={constrainX}
      >
        {data && (
          <>
            <tspan x="0" textAnchor="start">
              <tspan>
                <tspan className="graph-tooltip-colored-line-secondary">
                  ━
                </tspan>
                <tspan dx={10} className="graph-tooltip-value-description">
                  {firstLineLegend}
                  {': '}
                </tspan>
              </tspan>
              <tspan dx={20} className="graph-tooltip-value">
                {firstLineValue}
              </tspan>
            </tspan>

            {isMultiLine && (
              <tspan x="0" dy="20" textAnchor="start">
                <tspan>
                  <tspan className="graph-tooltip-colored-line-primary">
                    ━
                  </tspan>
                  <tspan dx={10} className="graph-tooltip-value-description">
                    {secondLineLegend}
                    {': '}
                  </tspan>
                </tspan>
                <tspan dx={20} className="graph-tooltip-value">
                  {secondLineValue}
                </tspan>
              </tspan>
            )}

            <tspan x="0" dy="20" textAnchor="start">
              {formattedDate}
            </tspan>
            <tspan x="0" dy="20" textAnchor="start">
              {isMultiLine
                ? data.y.firstLine.optionalText ||
                  data.y.secondLine.optionalText
                : data.y.firstLine.optionalText}
            </tspan>
          </>
        )}
      </D3Tooltip>
    </>
  );
}
